// Third Party
import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

// Types
import User from '@/types/user';
import ReducedComparison from '@/types/reduced-comparison';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';

// Components
import ActionsButton, { buttonAction } from '@/views/components/buttons/ActionsButton';
import { ComparisonsTable } from '../../../components/tables';
import { LoadingSpinner } from '../../../components/common';
import { SearchInput } from '../../../components/inputs';
import { ComparisonsTableFields } from '../../../components/tables/ComparisonsTable';
import { PrimaryLink } from '@/views/components/buttons';

// Constants
import { MAX_VISIBLE_COMPARISONS, MAX_LOADED_COMPARISONS } from '@/utils/constants';

// Utils
import { FormattedMessage } from '@/utils/i18n';
import ThemeMode from '@/types/theme-mode';

type Props = {
  user: User;
  title: string;
  className?: string;
  comparisons: ReducedComparison[];
  searchPlaceholder: string;
  loadComparisons: (query?: string, useNextToken?: boolean) => Promise<ReducedComparison[]>;
  hasMore?: boolean;
  tableFields?: ComparisonsTableFields[];
  onOpenComparison: (comparison: ReducedComparison) => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onSaveComparison: (id: string, name: string, contact?: GenericContact) => Promise<Comparison>;
  openingComparisonIds?: string[];
  showCreateComparison: boolean;
  onCreateComparison?: () => void;
  showStartBMOComparison: boolean;
  onCreateBMOComparison?: () => void;
  sortable?: boolean;
  responsive?: boolean;
  chevronOnHover?: boolean;
  enableShowMoreButton?: boolean;
  filterComponent?: JSX.Element;
  isScrollable?: boolean;
};

type State = {
  loading: boolean;
  showMore?: boolean;
};

class ComparisonsSection extends React.Component<Props, State> {
  // Set default props
  static defaultProps = {
    showCreateComparison: false,
    showStartBMOComparison: false,
  };

  private searchInputRef: SearchInput | null = null;

  private _ismounted: boolean = false;

  debounceSearch = debounce(() => {
    const query = this.getSearchQuery();

    this.props.loadComparisons(query).then(() => {
      this.setState({ loading: false });
    });
  }, 300);

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  componentDidMount() {
    this._ismounted = true;
    this.loadComparisons();
  }

  componentWillUnmount() {
    this._ismounted = false;
  }

  getSearchQuery = () => {
    if (!this.searchInputRef) {
      return '';
    }

    return this.searchInputRef.getInputValue();
  };

  loadComparisons = async (useNextToken?: boolean) => {
    const query = this.getSearchQuery();

    this.setState({ loading: true });

    await this.props.loadComparisons(query, useNextToken);

    if (this._ismounted) {
      // TODO This is needed to fix the no-op issue, but we shouldn't be calling setState on unmounted components
      const { responsive, comparisons } = this.props;

      this.setState({
        loading: false,
        showMore: responsive && comparisons.length > MAX_VISIBLE_COMPARISONS,
      });
    }
  };

  onLoadMore = () => {
    this.loadComparisons(true);
  };

  searchComparisons = () => {
    const { loading } = this.state;

    if (!loading) {
      this.setState({ loading: true });
    }

    this.debounceSearch();
  };

  getActionsForButton = (): buttonAction[] => {
    const { showCreateComparison, onCreateComparison, showStartBMOComparison, onCreateBMOComparison } = this.props;
    return [
      {
        condition: showCreateComparison,
        icon: 'add',
        node: <FormattedMessage id="Comparison.CreateComparison" />,
        onClick: onCreateComparison,
      },
      {
        condition: showStartBMOComparison,
        icon: 'bmo',
        node: <FormattedMessage id="Comparison.StartBMOGIF" />,
        onClick: onCreateBMOComparison,
      },
    ];
  };

  renderTitle = (isLightApp: boolean) => {
    const { title } = this.props;
    return (
      <>
        {isLightApp ? (
          <h1 data-private data-dd-privacy="mask">
            {title}
          </h1>
        ) : (
          <h4 data-private data-dd-privacy="mask">
            {title}
          </h4>
        )}
      </>
    );
  };

  render() {
    const {
      chevronOnHover,
      className,
      comparisons,
      enableShowMoreButton,
      hasMore,
      isScrollable,
      openingComparisonIds,
      responsive,
      searchPlaceholder,
      sortable,
      tableFields,
      user,
      onDeleteComparison,
      onOpenComparison,
      onSaveComparison,
    } = this.props;

    // TODO : remove all references to this once the classic mode disappears and keep light app as the default
    const isLightApp = user.preferences?.theme === ThemeMode.LIGHT;

    const { loading, showMore } = this.state;

    const showLoadingSpinner = loading && comparisons.length === 0;
    const enableShowMore = enableShowMoreButton && showMore && responsive && comparisons.length > MAX_VISIBLE_COMPARISONS;

    return (
      <div className={classNames('comparisons-section', className, { 'is-scrollable': isScrollable })}>
        <div className="comparisons-section-header">
          {this.renderTitle(isLightApp)}

          {this.props.filterComponent}

          <SearchInput
            compact
            className={isLightApp ? 'search-input-v2' : ''}
            ref={ref => {
              this.searchInputRef = ref;
            }}
            loading={loading}
            placeholder={searchPlaceholder}
            onChange={this.searchComparisons}
            data-private
            data-dd-privacy="mask"
          />

          <ActionsButton actions={this.getActionsForButton()} />
        </div>

        {!showLoadingSpinner && (
          <ComparisonsTable
            compact
            user={user}
            fields={tableFields}
            comparisons={comparisons}
            onLoadMore={this.onLoadMore}
            hasMore={hasMore && comparisons.length > MAX_LOADED_COMPARISONS - 1}
            onOpenComparison={onOpenComparison}
            onDeleteComparison={onDeleteComparison}
            onSaveComparison={onSaveComparison}
            scrollAncestor={window} // TODO Since the lightmode changes, the scrolling entity is
            // the .rt-body of the table, but it is not an ancestor of the loading
            // waypoint. We should probably have the waypoint inside the table to
            // trigger the "load more" on scroll of the table
            openingComparisonIds={openingComparisonIds}
            chevronOnHover={chevronOnHover}
            responsive={responsive}
            sortable={sortable}
          />
        )}

        {showLoadingSpinner && <LoadingSpinner className="content-spinner" />}

        {enableShowMore && (
          <PrimaryLink className="show-more" onClick={() => this.setState({ showMore: false })}>
            <FormattedMessage id="ComparisonHome.ShowMore" />
          </PrimaryLink>
        )}
      </div>
    );
  }
}

export default ComparisonsSection;
